import type { FC } from "react";
import { useLayoutEffect, useMemo, useState } from "react";

import type { Options } from "highcharts";

import { LineGraph } from "@similarweb/pro-logic-components";

import { LLMAnnotationsContextProvider } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMAnnotationContextProvider";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { getLineGraphConfig } from "./config";
import { getSharedLegendProps, VIEW_MODE_CONFIG } from "./consts";
import { useLegendSelection } from "./hooks/useLegendSelection";
import type { SeriesData, SeriesItem } from "./types";
import { EViewMode } from "./types";

interface IOvertimeChartSectionProps {
    campaign: IAiTrackerCampaign;
    transformedData: Record<EViewMode.VisibilityOvertime, SeriesData> | undefined;
    selectedTopic: string;
    selectedGranularity: GranularityNames;
    reportDuration: DatesRange;
}

const legendValueFilter =
    VIEW_MODE_CONFIG[EViewMode.VisibilityOvertime]?.displayValueFilter ?? ((value: number) => value.toString());

export const OvertimeChartSection: FC<IOvertimeChartSectionProps> = ({
    campaign,
    transformedData,
    selectedTopic,
    selectedGranularity,
    reportDuration,
}) => {
    const [mutableTransformedData, setMutableTransformedData] = useState(transformedData);

    useLayoutEffect(() => {
        setMutableTransformedData(transformedData);
    }, [transformedData]);

    const topicFilteredData = useMemo<SeriesItem[]>(() => {
        const lowercaseTopicName = selectedTopic.toLowerCase();
        const viewModeData = mutableTransformedData?.[EViewMode.VisibilityOvertime];
        return viewModeData
            ? (Object.entries(viewModeData).find(([key]) => key.toLowerCase() === lowercaseTopicName)?.[1] ?? [])
            : [];
    }, [mutableTransformedData, selectedTopic]);

    const { onLegendSelection } = useLegendSelection({
        topicFilteredData,
        selectedTopic,
        selectedViewMode: EViewMode.VisibilityOvertime,
        setTransformedData: setMutableTransformedData,
    });

    const graphConfig = useMemo(
        () => getLineGraphConfig(topicFilteredData, selectedGranularity, reportDuration) as Options,
        [topicFilteredData, selectedGranularity, reportDuration],
    );

    const showMarkers = topicFilteredData?.some(({ visible, data: itemData }) => visible && itemData.length === 1);
    const coreAverageIndex = topicFilteredData.findIndex((item) => item.isCoreAverage);
    const separatorAboveIndex = coreAverageIndex >= 0 ? coreAverageIndex : null;
    
    return (
        <LLMAnnotationsContextProvider
            key={`model-comparison-graph-${campaign?.id}`}
            chartName="model-comparison-overtime"
            granularity={selectedGranularity}
            campaign={campaign}
        >
            <LineGraph<SeriesItem>
                key={`model-comparison-line-${EViewMode.VisibilityOvertime}-${selectedGranularity}`}
                data={topicFilteredData}
                graphConfig={graphConfig}
                withMarkers={!!showMarkers}
                legendValueFilter={legendValueFilter}
                legendToggleHandlerCallback={onLegendSelection}
                isControlledGraph={true}
                presentLineSeparatorAboveItemIndex={separatorAboveIndex}
                {...getSharedLegendProps()}
            />
        </LLMAnnotationsContextProvider>
    );
};
